/**
 * Overtime Report API Types
 * 
 * Transport DTOs (LC_*) for backend communication
 * and internal UI models for overtime (HHEE) reports.
 */

import type { APIExportFormat } from '@/types/fileExport.types';

// ============================================================================
// TRANSPORT DTOs (Backend)
// ============================================================================

/**
 * Request parameters for overtime report
 */
export interface LC_GetOvertimeReportIn {
  year: number;
  month: number;
  monthlyCutId: number;
  type: OvertimeRecordType;
  page: number;
  pageSize: number;
  orderBy?: string;
  orderDirection: 'ASC' | 'DESC';
}

/**
 * Concept with time values in both formats
 */
export interface LC_OvertimeConceptOut {
  hhmmss: string;
  decimalValue: number;
}

/**
 * All overtime concepts for a record
 */
export interface LC_OvertimeConceptsOut {
  overtimeT1: LC_OvertimeConceptOut;
  overtimeT2: LC_OvertimeConceptOut;
  overtimeT3: LC_OvertimeConceptOut;
  delay: LC_OvertimeConceptOut;
  absenceBetween: LC_OvertimeConceptOut;
  earlyDeparture: LC_OvertimeConceptOut;
}

/**
 * Single overtime record item
 */
export interface LC_OvertimeRecordOut {
  id: string;
  type: string;
  date: string;
  schedule: string;
  hoursHHMMSS: string;
  hoursDecimal: number;
  concepts: LC_OvertimeConceptsOut;
}

/**
 * Indicators/totals for the report period
 */
export interface LC_OvertimeIndicatorsOut {
  overtimeTotal: string;
  overtimeTotalDecimal: number;
  delaysTotal: string;
  delaysTotalDecimal: number;
}

/**
 * Data payload from backend
 */
export interface LC_OvertimeReportDataOut {
  indicators: LC_OvertimeIndicatorsOut;
  records: LC_OvertimeRecordOut[];
  totalRecords: number;
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
}

/**
 * Response from overtime report endpoint (nested under data.data)
 */
export interface LC_GetOvertimeReportOut {
  data: LC_OvertimeReportDataOut;
}

/**
 * Request parameters for exporting employee HHEE summary
 */
export interface LC_ExportEmployeeHheeSummaryIn {
  monthlyCutId: number;
  companyId?: number;
  employeeId?: number;
  type?: 'Todos' | 'HHEE' | 'Atrasos';
  format: APIExportFormat;
}

// ============================================================================
// ENUMS / CONSTANTS
// ============================================================================

export const OVERTIME_RECORD_TYPES = ['Todos', 'HHEE', 'Atrasos'] as const;
export type OvertimeRecordType = typeof OVERTIME_RECORD_TYPES[number];

// ============================================================================
// INTERNAL UI MODELS
// ============================================================================

/**
 * Concept for UI display
 */
export interface OvertimeConcept {
  timeFormatted: string;
  decimalValue: number;
}

/**
 * All concepts for UI
 */
export interface OvertimeConcepts {
  overtimeT1: OvertimeConcept;
  overtimeT2: OvertimeConcept;
  overtimeT3: OvertimeConcept;
  delay: OvertimeConcept;
  absenceBetween: OvertimeConcept;
  earlyDeparture: OvertimeConcept;
}

/**
 * Single record for UI table
 */
export interface OvertimeRecord {
  id: string;
  type: string;
  date: string;
  schedule: string;
  hoursFormatted: string;
  hoursDecimal: number;
  concepts: OvertimeConcepts;
}

/**
 * Indicators for UI cards
 */
export interface OvertimeIndicators {
  overtimeTotal: string;
  overtimeTotalDecimal: number;
  delaysTotal: string;
  delaysTotalDecimal: number;
}

/**
 * Complete overtime report result for UI
 */
export interface OvertimeReportResult {
  indicators: OvertimeIndicators;
  records: OvertimeRecord[];
  totalRecords: number;
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
}
